// @ts-check
// Nota no painel lateral (doc 10, seções 6.2 e 6.6): título e corpo formatado, salvos sozinhos (300 ms depois de
// parar de digitar). Nota nova só é criada, com o vínculo ao registro, quando ganha título ou texto.
import { html } from 'htm/preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { objectsByNameSingular } from '../../data/metadata-store.js';
import { findOneRecord } from '../../data/rest.js';
import { deleteRecordOptimistically, updateRecordOptimistically } from '../../data/record-store.js';
import { formatDateTime } from '../../lib/format.js';
import { Icon } from '../../ui/icon.js';
import { IconButton } from '../../ui/button.js';
import { Skeleton } from '../../ui/feedback.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { closeSidePanel } from '../side-panel/side-panel-state.js';
import { createActivityForRecord, getActivityBodyField, getBodyText } from './activity-targets.js';
import { RichTextEditor } from './rich-text-editor.js';
import { ActivityTargetsCell } from './activity-targets-cell.js';
import { t } from '../../i18n/index.js';

const PERSIST_DELAY_MILLISECONDS = 300;

/**
 * @param {{ noteId: string | null, targetObjectNameSingular: string, targetRecordId: string, onSaved?: (note: any, change?: 'deleted') => void }} props
 */
export function NoteEditorPanel({ noteId, targetObjectNameSingular, targetRecordId, onSaved }) {
  const noteObject = objectsByNameSingular.value.get('note');
  const bodyField = noteObject ? getActivityBodyField(noteObject) : undefined;
  const [note, setNote] = useState(/** @type {any | null} */ (null));
  const [isLoading, setIsLoading] = useState(Boolean(noteId));
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [status, setStatus] = useState(/** @type {'idle' | 'saving' | 'saved'} */ ('idle'));
  const pendingRef = useRef(/** @type {ReturnType<typeof setTimeout> | null} */ (null));
  const noteRef = useRef(/** @type {any | null} */ (null));
  const latestRef = useRef({ title: '', body: '' });
  const creatingRef = useRef(/** @type {Promise<any> | null} */ (null));

  useEffect(() => {
    if (!noteId || !noteObject) return;
    findOneRecord(noteObject, noteId, { depth: 0 })
      .then((loaded) => {
        noteRef.current = loaded;
        setNote(loaded);
        setTitle(loaded.title ?? '');
        setBody(bodyField ? getBodyText(loaded[bodyField.name]) : '');
        latestRef.current = { title: loaded.title ?? '', body: bodyField ? getBodyText(loaded[bodyField.name]) : '' };
      })
      .catch(showErrorSnackbar)
      .finally(() => setIsLoading(false));
  }, [noteId]);

  const persist = async () => {
    pendingRef.current = null;
    if (!noteObject) return;
    const { title: nextTitle, body: nextBody } = latestRef.current;
    const data = { title: nextTitle.trim(), ...(bodyField ? { [bodyField.name]: { markdown: nextBody, blocknote: null } } : {}) };
    setStatus('saving');
    try {
      if (!noteRef.current) {
        if (!nextTitle.trim() && !nextBody.trim()) {
          setStatus('idle');
          return;
        }
        // várias teclas antes da criação terminar não podem criar duas notas
        creatingRef.current ??= createActivityForRecord('note', targetObjectNameSingular, targetRecordId, data);
        const created = await creatingRef.current;
        noteRef.current = created;
        setNote(created);
        onSaved?.(created);
        if (latestRef.current.title !== nextTitle || latestRef.current.body !== nextBody) schedulePersist();
      } else {
        const updated = await updateRecordOptimistically(noteObject, noteRef.current.id, data, { title: data.title });
        noteRef.current = updated ?? noteRef.current;
        onSaved?.(noteRef.current);
      }
      setStatus('saved');
    } catch (error) {
      setStatus('idle');
      showErrorSnackbar(error);
    }
  };

  const schedulePersist = () => {
    if (pendingRef.current) clearTimeout(pendingRef.current);
    pendingRef.current = setTimeout(persist, PERSIST_DELAY_MILLISECONDS);
  };

  // fechar o painel com uma gravação pendente grava na hora
  useEffect(() => () => {
    if (pendingRef.current) {
      clearTimeout(pendingRef.current);
      persist();
    }
  }, []);

  const remove = async () => {
    if (!noteObject || !noteRef.current) {
      closeSidePanel();
      return;
    }
    try {
      await deleteRecordOptimistically(noteObject, noteRef.current.id);
      onSaved?.(noteRef.current, 'deleted');
      showSuccessSnackbar(t('parity.notes.deleted'));
      closeSidePanel();
    } catch {
      // o snackbar de erro já foi mostrado
    }
  };

  return html`<div class="side-panel-page note-editor-panel">
    <div class="side-panel-top-bar">
      <div class="side-panel-top-bar-left">
        <span class="side-panel-page-chip">
          <${Icon} name="IconNotes" size=${16} />
          <span>${note ? t('parity.notes.editTitle') : t('notes.new')}</span>
        </span>
        <span class="note-editor-status" aria-live="polite">${status === 'saving' ? t('parity.notes.saving') : status === 'saved' ? t('parity.notes.saved') : ''}</span>
      </div>
      <div class="side-panel-top-bar-left">
        <${IconButton} icon="IconTrash" size="small" variant="tertiary" ariaLabel=${t('parity.notes.delete')} onClick=${remove} />
        <${IconButton} icon="IconX" size="small" variant="tertiary" ariaLabel=${t('sidePanel.close')} onClick=${closeSidePanel} />
      </div>
    </div>
    <div class="side-panel-content note-editor-content">
      ${isLoading
        ? html`<${Skeleton} width="60%" height=${20} /><${Skeleton} width="90%" height=${14} />`
        : html`
            <input
              class="note-editor-title"
              placeholder=${t('notes.titlePlaceholder')}
              value=${title}
              aria-label=${t('notes.titlePlaceholder')}
              ref=${(/** @type {HTMLInputElement | null} */ element) => {
                if (element && !noteId && !element.dataset.focused) {
                  element.dataset.focused = 'true';
                  requestAnimationFrame(() => element.focus());
                }
              }}
              onInput=${(event) => {
                setTitle(event.currentTarget.value);
                latestRef.current = { ...latestRef.current, title: event.currentTarget.value };
                schedulePersist();
              }}
            />
            ${note ? html`<${ActivityTargetsCell} activityNameSingular="note" activityId=${note.id} />` : null}
            ${note?.createdAt ? html`<span class="note-editor-meta">${t('parity.notes.createdAt', { date: formatDateTime(note.createdAt) })}</span>` : null}
            <${RichTextEditor}
              value=${body}
              placeholder=${t('parity.notes.bodyPlaceholder')}
              onChange=${(markdown) => {
                setBody(markdown);
                latestRef.current = { ...latestRef.current, body: markdown };
                schedulePersist();
              }}
            />
          `}
    </div>
  </div>`;
}
